import { useEffect, useState } from 'react';

import { useDocumentOcrStore, useMyMoneyFormStore } from '@/modules/UseMyMoney/stores';
import { OcrResult } from '@/modules/UseMyMoney/UseMyMoney.types';

interface Props {
  documentOcrOptions?: OcrResult;
}

const useSetupClaimSummaryFieldsFromOCR = ({ documentOcrOptions }: Props) => {
  const { ocrDocument, setOcrDocument } = useDocumentOcrStore();
  const { values, handleSetInitialFormValues } = useMyMoneyFormStore();

  const [isAmountSet, setIsAmountSet] = useState(false);
  const [isProviderSet, setIsProviderSet] = useState(false);
  const [isPatientSet, setIsPatientSet] = useState(false);
  const [isDateOfServiceSet, setIsDateOfServiceSet] = useState(false);
  const [isServiceSet, setIsServiceSet] = useState(false);

  const amounts = ocrDocument?.amountOptions;
  const providers = ocrDocument?.providers;
  const patients = ocrDocument?.serviceForOptions;
  const serviceDates = ocrDocument?.serviceDateOptions;
  const services = ocrDocument?.serviceOptions;

  // set amount from OCR
  useEffect(() => {
    if (isAmountSet || values?.amount || !ocrDocument?.selectedAmount?.amount) return;
    const amount = amounts?.find((amount) => amount.amount === ocrDocument?.selectedAmount?.amount);
    handleSetInitialFormValues({
      amount: ocrDocument?.selectedAmount?.amount,
      customAmount: amount?.isSuggested ? undefined : ocrDocument?.selectedAmount?.amount,
    });
    setIsAmountSet(true);
  }, [isAmountSet, values?.amount, ocrDocument?.selectedAmount?.amount, amounts, handleSetInitialFormValues]);

  // set provider from OCR
  useEffect(() => {
    if (isProviderSet || values?.providerId || !providers?.length || !ocrDocument?.selectedProvider?.providerId) return;
    const provider = providers?.find((provider) => provider?.id === ocrDocument?.selectedProvider?.providerId);
    handleSetInitialFormValues({
      providerId: provider?.id,
      customProviderId: provider?.isSuggested ? undefined : provider?.id,
    });
    setIsProviderSet(true);
  }, [isProviderSet, values?.providerId, ocrDocument?.selectedProvider?.providerId, providers, handleSetInitialFormValues]);

  // set patient from OCR
  useEffect(() => {
    if (isPatientSet || values?.patientId || !patients?.length || !ocrDocument?.selectedServiceFor?.id) return;
    const patient = patients?.find((patient) => patient?.id === ocrDocument?.selectedServiceFor?.id);
    handleSetInitialFormValues({
      patientId: patient?.id,
      customPatientId: patient?.isSuggested ? undefined : patient?.id,
    });
    setIsPatientSet(true);
  }, [isPatientSet, values?.patientId, ocrDocument?.selectedServiceFor?.id, patients, handleSetInitialFormValues]);

  // set date of service from OCR
  useEffect(() => {
    if (isDateOfServiceSet || values?.dateOfService || !ocrDocument?.selectedServiceDate?.date) return;
    const dateOfService = serviceDates?.find((dateOfService) => dateOfService.date === ocrDocument?.selectedServiceDate?.date);
    handleSetInitialFormValues({
      dateOfService: ocrDocument?.selectedServiceDate?.date,
      customDateOfService: dateOfService?.isSuggested ? undefined : ocrDocument?.selectedServiceDate?.date,
    });
    setIsDateOfServiceSet(true);
  }, [isDateOfServiceSet, values?.dateOfService, serviceDates, ocrDocument?.selectedServiceDate?.date, handleSetInitialFormValues]);

  // set service from OCR
  useEffect(() => {
    if (isServiceSet || values?.serviceId || !services?.length || !ocrDocument?.selectedService?.serviceId) return;
    const service = services?.find((service) => service?.serviceId === ocrDocument?.selectedService?.serviceId);
    handleSetInitialFormValues({
      serviceId: service?.serviceId,
      customServiceId: service?.isSuggested ? undefined : service?.serviceId,
    });
    setIsServiceSet(true);
  }, [isServiceSet, values?.serviceId, ocrDocument?.selectedService?.serviceId, services, handleSetInitialFormValues]);

  useEffect(() => {
    if (documentOcrOptions) {
      setOcrDocument(documentOcrOptions);
    }
  }, [documentOcrOptions, setOcrDocument]);
};

export default useSetupClaimSummaryFieldsFromOCR;
